/* eslint-disable multiline-ternary */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  Img,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  type ButtonProps
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Howl } from 'howler'
import {
  ExternalLink,
  History,
  Info,
  MonitorPause,
  MonitorPlay,
  SkipForward,
  Volume
} from 'lucide-react'
import React, {
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement
} from 'react'
import ReactPlayer from 'react-player'
import QRCode from 'react-qr-code'
import { stringSimilarity } from 'string-similarity-js'
import { usePageContext } from 'vike-react/usePageContext'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { activities, universalRoomOptions } from '../../activities'
import {
  ErrorModal,
  ScreenSizePlayalongErrorModal
} from '../../components/Modals'
import SideNav from '../../components/SideNav'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { CircleTimer } from './CircleTimer'
import { episodes } from './episodes'
import {
  type Episode,
  type Event,
  ANSWER_TIME,
  RESULTS_TIME,
  SCORES_TIME
} from './types'
import { title } from './+title'
import EpisodeList from './EpisodeList'

const enum YoutubePlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5
}

const mobileMaxVideoHeight = '300px'

const alphaToNumeric = (str: string): number => {
  const char = str.toLowerCase().charCodeAt(0)
  return char - 97 // 'a' is 97 in ASCII
}

const numericToAlpha = (num: number): string => {
  return String.fromCharCode(num + 97)
}

let CHANNEL_GLOBAL: any = null

interface CircleButtonProps extends ButtonProps {
  icon: any
  label: string
  hoverWidth: string
  showHover?: boolean
}

// const OAUTH_TOKEN = ''
// const BOT_USERNAME = ''
// const CHANNEL_NAME = ''

// const twitchWssUrl = 'wss://irc-ws.chat.twitch.tv:443'
// const chat = new WebSocket(twitchWssUrl)

const stageMotion = {
  hiddenMobile: {
    opacity: 0,
    left: '0%',
    x: '-100%',
    transition: {
      type: 'easeIn',
      duration: 0.5
    }
  },
  hidden: {
    opacity: 0,
    left: '0%',
    x: '-100%',
    y: '-50%',
    zIndex: '200',
    transition: {
      type: 'easeIn',
      duration: 0.5
    }
  },
  hiddenSupport: {
    opacity: 0,
    left: '0%',
    x: '-100%',
    y: '0px',
    transition: {
      type: 'easeIn',
      duration: 0.5
    }
  },
  hiddenScore: {
    opacity: 0,
    left: '0%',
    x: '-100%',
    y: '0px',
    transition: {
      type: 'easeIn',
      duration: 0.5
    }
  },
  visible: {
    opacity: 1,
    left: '0%',
    x: '0',
    transition: {
      type: 'easeOut',
      duration: 0.5
    }
  }
}

// chat.addEventListener('open', () => {
//   // Successful connection to twitch IRC endpoint
//   // Connecting to user channel
//   chat.send(`PASS oauth:${OAUTH_TOKEN}`)
//   chat.send(`NICK ${BOT_USERNAME}`)
//   chat.send(`JOIN #${CHANNEL_NAME}`)

//   console.log(`Successful connection to: ${CHANNEL_NAME}`)
// })

// chat.addEventListener('error', (e) => {
//   console.log('Connect error:', JSON.stringify(e))
//   chat.close()
// })

// chat.addEventListener('close', () => {
//   console.log('Connection Terminated')
// })

interface PlayerScore {
  username: string
  score: number
}

let isConnecting = false
const initialRoomSettings = {
  activity: activities.playalong,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.hostInfo.map((text: string, index: number) => {
          return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
        })}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions,
    promptControl: {
      label: 'Pause Control',
      type: 'select',
      value: 'automated',
      tooltip:
        'Automated will automatically resume playback after a prompt. Host Controlled will require the host to manually resume playback.',
      options: [
        {
          label: 'Automated',
          value: 'automated'
        },
        {
          label: 'Host Controlled',
          value: 'host'
        }
      ]
    },
    promptDuration: {
      label: (
        <>
          Guessing Time
          <br />
          <sup>(In Seconds)</sup>
        </>
      ),
      tooltip: 'This is the amount of time players have to guess',
      type: 'number',
      value: 30,
      min: 10,
      max: 90,
      step: 10,
      hidden: (settings: any): boolean =>
        settings.options.promptControl.value === 'host'
    },
    promptDurationDelay: {
      label: (
        <>
          Guessing Time Delay
          <br />
          <sup>(In Seconds)</sup>
        </>
      ),
      tooltip:
        'This is to account for the delay on streaming services such as Twitch (which is about 10 seconds)',
      type: 'number',
      value: 0,
      min: 0,
      max: 30,
      step: 5,
      hidden: (settings: any): boolean =>
        settings.options.promptControl.value === 'host'
    },
    videoVolume: {
      label: 'Video Volume',
      type: 'number',
      value: 100,
      min: 0,
      max: 100,
      step: 5
    },
    musicVolume: {
      label: 'Music Volume',
      type: 'number',
      value: 100,
      min: 0,
      max: 100,
      step: 5
    },
    mode: {
      label: 'Interaction Mode',
      type: 'select',
      value: 'browser',
      hidden: () => true,
      options: [
        {
          label: 'Browser',
          value: 'browser'
        },
        {
          label: 'Twitch Chat (Coming Soon)',
          value: 'twitch',
          disabled: true
        }
        // {
        //   label: "Twitch Chat",
        //   value: 'twitch'
        // }
      ]
    },
    splitScreen: {
      label: (
        <>
          Play On Screen
          <br />
          <sup>Watch and play on the same screen</sup>
        </>
      ),
      tooltip: 'Best for playing on a single screen solo',
      type: 'bool',
      value: false
    },
    playImport: {
      label: 'Play a Custom Episode',
      buttonLabel: 'Import Episode',
      type: 'button',
      value: () => {}
    }
  }
}

const isDevMode = import.meta.env.MODE === 'development'

const NEXT_EVENT_BUFFER_TIME = isDevMode ? 1 : 5

enum GameStates {
  ATTRACT,
  LOADING,
  SETUP,
  PLAYING,
  PAUSED,
  LIVE_PROMPT,
  LIVE_PAUSED,
  ANSWER,
  PAUSE_PROMPT,
  RESULTS,
  SCORES,
  END
}

type Result = Record<
  string,
  {
    answer: string | number
    score: number
  }
>

type PreviousEvents = Record<
  string,
  {
    event: Event
    result: Result
  }
>

interface GameVars {
  currentTime: number
  stateInterval: NodeJS.Timeout | undefined
  upcomingEvents: Event[]
  previousEvents: PreviousEvents
  currentEvent: Event | undefined
}

const BLANK_GAME_VARS: GameVars = {
  currentTime: 0,
  stateInterval: undefined,
  upcomingEvents: [],
  previousEvents: {},
  currentEvent: undefined
}

// let gameVars.current.currentTime = 0
// let gameVars.current.stateInterval: NodeJS.Timeout
// let questions

const currentVolume = 0

const Page: React.FC = () => {
  const [hasInteracted, setHasInteracted] = useState(false)
  const pageContext = usePageContext()
  const { roomSettings, setRoomSettings, setShowIntroModal, showIntroModal } =
    useContext(RoomSettingsContext)
  const [roomCode, setRoomCode] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [playerScores, setPlayerScores] = useState<PlayerScore[]>([])
  const [showPrompt, setShowPrompt] = useState<boolean>(false)
  const [showScores, setShowScores] = useState<boolean>(false)
  const [showResults, setShowResults] = useState<boolean>(false)
  const [prevQuickResults, setPrevQuickResults] = useState<string[]>([])
  const [currentResults, setCurrentResults] = useState<string>('-1')
  const currentResultsRef = useRef<string>(currentResults)
  const [isPaused, setIsPaused] = useState<boolean>(false)

  const [showAnswers, setShowAnswers] = useState<boolean>(false)
  const [tieredAnswerIndex, setTieredAnswerIndex] = useState<number>(-1)
  const [totalSeconds, setTotalSeconds] = useState<number>(0)
  const [episode, setEpisode] = useState<Episode | undefined>(undefined)
  const [currentEpisodeSlug, setCurrentEpisodeSlug] = useState<string>(
    pageContext?.routeParams?.id ?? ''
  )

  const gameVars = useRef<GameVars>(structuredClone(BLANK_GAME_VARS))
  const videoRef = useRef<any>()
  const audioRef = useRef<any>({})
  const musicRef = useRef<any>()
  const musicTimeoutRef = useRef<NodeJS.Timeout>()
  const [currentMusicUrl, setCurrentMusicUrl] = useState<string>('')
  const [gameState, setGameState] = useState<GameStates>(GameStates.ATTRACT)
  const gameStateRef = useRef<GameStates>(gameState)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetRoom = (): void => {
    pauseMusic()
    clearInterval(gameVars.current.stateInterval)
    videoRef.current?.getInternalPlayer()?.pauseVideo()
    musicRef.current?.getInternalPlayer()?.pauseVideo()
    audioRef.current.theme?.stop()
    audioRef.current.prompt?.stop()
    setPlayerScores([])
    setShowPrompt(false)
    setShowScores(false)
    setShowResults(false)
    setPrevQuickResults([])
    setCurrentResults('-1')
    currentResultsRef.current = '-1'
    setIsPaused(false)
    setShowAnswers(false)
    setTieredAnswerIndex(-1)
    setRoomCode('')
    setTotalSeconds(0)
    setErrorMessage(null)
    setGameState(GameStates.ATTRACT)
    gameVars.current = structuredClone(BLANK_GAME_VARS)
    setCurrentEpisodeSlug('')
    setCurrentMusicUrl('')
    audioRef.current = {}
    videoRef.current = undefined
    musicRef.current = undefined
    connection('pa', PlayAlongOutCallback, { triggerAction, triggerEnter })
  }

  const handleImportClick = (): void => {
    if (fileInputRef.current != null) {
      fileInputRef.current.click() // Programmatically open the file input
    }
  }

  const validateImportFile = (episode: Episode): boolean => {
    try {
      if (episode.videoUrl === '') {
        return false
      }
      if (episode.title === '') {
        return false
      }
      if (episode.series.name === '') {
        return false
      }
      if (episode.series.creator.name === '') {
        return false
      }
      if (episode.events.length === 0) {
        return false
      }

      return true
    } catch (error) {
      return false
    }
  }

  const handleImportFileChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files !== null ? event.target.files[0] : null
    if (file !== null) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          if (e.target === null || e.target.result === null) {
            throw new Error('No file loaded')
          }
          const tempEpisode = JSON.parse(e.target.result as string) // Parse JSON content
          if (!validateImportFile(tempEpisode)) {
            throw new Error('Invalid episode file')
          }
          episodes.import = tempEpisode
          setEpisode(tempEpisode)
          setCurrentEpisodeSlug('import')
          setGameState(GameStates.LOADING)
          if (fileInputRef.current != null) {
            fileInputRef.current.value = '' // Reset the file input
          }
          window.history.pushState({}, '', '/playalong/import')
        } catch (error) {
          console.error('Error parsing JSON:', error)
          alert('Invalid JSON file.')
        }
      }
      reader.readAsText(file) // Read the file as text
    }
  }

  const handleDebugEpisode = (): void => {
    const episodeData = localStorage.getItem('savedDebugEpisode')
    if (episodeData !== null) {
      setEpisode(JSON.parse(episodeData))
      episodes.import = JSON.parse(episodeData)
      setCurrentEpisodeSlug('import')
      setGameState(GameStates.LOADING)
    }
  }

  const triggerAction = (message: {
    clientId: string
    guess: string
  }): void => {
    if (message.guess.includes('!guess ')) {
      if (
        [
          GameStates.LIVE_PROMPT,
          GameStates.LIVE_PAUSED,
          GameStates.PAUSE_PROMPT
        ].includes(gameStateRef.current)
      ) {
        if (gameVars.current.currentEvent === undefined) {
          return
        }
        const username = message.clientId
        const guess = message.guess
          .split('!guess ')[1]
          .replace(/(\r\n|\n|\r)/gm, '')
          .trim()
        const score = calculateScore(
          gameVars.current.previousEvents[gameVars.current.currentEvent.id]
            .event,
          guess
        )
        gameVars.current.previousEvents[
          gameVars.current.currentEvent.id
        ].result[username] = {
          answer: guess,
          score
        }
      }
    }
  }
  const triggerEnter = (message: any): void => {
    if (
      [
        GameStates.LIVE_PROMPT,
        GameStates.LIVE_PAUSED,
        GameStates.PAUSE_PROMPT
      ].includes(gameStateRef.current)
    ) {
      sendPlayersPrompt()
    } else {
      sendPlayersWaiting()
    }
  }

  const PlayAlongOutCallback = (results: any): void => {
    // braindump, when backing out of game to episode list, make sure to reset everything
    if (results !== false) {
      setRoomCode(results.roomCode)
      CHANNEL_GLOBAL = results.channel
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }

  const setupMusic = (): void => {
    pauseMusic()
    if (episode === undefined) return
    if (episode?.introMusic?.source === 'current') {
      setCurrentMusicUrl(episode.videoUrl)
    }

    audioRef.current.theme =
      episode.introMusic != null &&
      episode.introMusic.source === 'mp3' &&
      episode.introMusic.url !== ''
        ? new Howl({
            src: [episode.introMusic.url],
            loop: true,
            autoplay: false,
            volume: roomSettings.options.musicVolume.value / 100
          })
        : null

    audioRef.current.prompt =
      episode.promptMusic != null &&
      episode.promptMusic.source === 'mp3' &&
      episode.promptMusic.url !== ''
        ? new Howl({
            src: [episode.promptMusic.url],
            loop: true,
            autoplay: false,
            volume: roomSettings.options.musicVolume.value / 100
          })
        : null
    if (episode.introMusic?.source === 'youtube') {
      setCurrentMusicUrl(episode.introMusic.url)
    } else if (episode.introMusic?.source === 'current') {
      setCurrentMusicUrl(episode.videoUrl)
    } else {
      setCurrentMusicUrl('')
    }
  }

  const updateMusicVolume = (): void => {
    if (
      audioRef.current.theme !== null &&
      audioRef.current.theme !== undefined
    ) {
      audioRef.current.theme.volume(
        roomSettings.options.musicVolume.value / 100
      )
    }
    if (
      audioRef.current.prompt !== null &&
      audioRef.current.prompt !== undefined
    ) {
      audioRef.current.prompt.volume(
        roomSettings.options.musicVolume.value / 100
      )
    }
    if (videoRef.current !== null && videoRef.current !== undefined) {
      videoRef.current
        ?.getInternalPlayer()
        ?.setVolume(roomSettings.options.videoVolume.value)
    }
    if (
      musicRef.current?.getInternalPlayer() !== undefined &&
      musicRef.current?.getInternalPlayer() !== null
    ) {
      musicRef.current
        ?.getInternalPlayer()
        ?.setVolume(roomSettings.options.musicVolume.value)
    }
  }

  const fadeMusic = (start: number, end: number, duration: number): void => {
    const fps = 60
    if (musicRef.current?.getInternalPlayer() !== undefined && duration > 0) {
      musicRef.current?.getInternalPlayer()?.setVolume(start)
      const step = (end - start) / duration
      setTimeout(() => {
        fadeMusic(start + step * fps, end, duration - 1000 / fps)
      }, 1000 / fps)
    }
  }

  const playPromptMusic = (): void => {
    pauseMusic()
    if (
      audioRef.current.prompt !== undefined &&
      audioRef.current.prompt !== null
    ) {
      audioRef.current.prompt.volume(0)
      audioRef.current.prompt.play()
      audioRef.current.prompt.fade(
        0,
        roomSettings.options.musicVolume.value / 100,
        1000
      )
    } else if (
      musicRef.current?.getInternalPlayer() !== undefined &&
      musicRef.current?.getInternalPlayer() !== null
    ) {
      musicRef.current
        ?.getInternalPlayer()
        ?.seekTo(episode?.promptMusic?.startTime ?? 0)
      musicRef.current?.getInternalPlayer()?.playVideo()
      musicRef.current?.getInternalPlayer()?.setVolume(0)

      if (episode?.promptMusic?.endTime !== undefined) {
        const endTime = episode.promptMusic.endTime
        const startTime = episode.promptMusic.startTime
        const duration = endTime - startTime - 1

        musicTimeoutRef.current = setTimeout(() => {
          fadeMusic(roomSettings.options.musicVolume.value, 0, 1000)
          musicTimeoutRef.current = setTimeout(() => {
            musicRef.current?.getInternalPlayer()?.pauseVideo()
            musicTimeoutRef.current = setTimeout(() => {
              playPromptMusic()
            }, 100)
          }, 1100)
        }, duration * 1000)
      }
    }
  }

  const playIntroMusic = (): void => {
    if (!hasInteracted) {
      return
    }
    if (window.innerWidth < 1024 || window.innerHeight < 768) {
      return
    }

    if (musicRef.current?.getInternalPlayer()?.getPlayerState !== undefined) {
      if (
        musicRef.current?.getInternalPlayer()?.getPlayerState() ===
        YoutubePlayerState.PLAYING
      ) {
        return
      }
      if (
        musicRef.current?.getInternalPlayer()?.getCurrentTime() !==
        episode?.introMusic?.startTime
      ) {
        musicRef.current
          ?.getInternalPlayer()
          ?.seekTo(episode?.introMusic?.startTime ?? 0)
      }
      musicRef.current?.getInternalPlayer()?.playVideo()
      musicRef.current
        ?.getInternalPlayer()
        ?.seekTo(episode?.introMusic?.startTime ?? 0)
      musicRef.current?.getInternalPlayer()?.playVideo()
      musicRef.current?.getInternalPlayer()?.setVolume(0)

      if (episode?.introMusic?.endTime !== undefined) {
        const endTime = episode.introMusic.endTime
        const startTime = episode.introMusic.startTime
        const duration = endTime - startTime - 1

        musicTimeoutRef.current = setTimeout(() => {
          fadeMusic(roomSettings.options.musicVolume.value, 0, 1000)
          musicTimeoutRef.current = setTimeout(() => {
            musicRef.current?.getInternalPlayer()?.pauseVideo()
            musicTimeoutRef.current = setTimeout(() => {
              playIntroMusic()
            }, 100)
          }, 1100)
        }, duration * 1000)
      }
    }
    if (audioRef.current.theme !== undefined) {
      audioRef.current.theme?.fade(
        0,
        roomSettings.options.musicVolume.value / 100,
        1000
      )
    }
  }

  const pauseMusic = (): void => {
    if (musicRef.current?.getInternalPlayer()?.pauseVideo !== undefined) {
      musicRef.current?.getInternalPlayer()?.pauseVideo()
      clearTimeout(musicTimeoutRef.current)
    }
    if (audioRef.current.prompt !== undefined) {
      audioRef.current.prompt?.stop()
    }
    if (audioRef.current.theme !== undefined) {
      audioRef.current.theme?.stop()
    }
  }

  useEffect(() => {
    const handleInteraction = (): void => {
      setHasInteracted(true)
      // Remove listeners after first interaction
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
    }

    // Add listeners
    document.addEventListener('click', handleInteraction)
    document.addEventListener('touchstart', handleInteraction)

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
    }
  }, [])

  // useEffect(() => {
  //   const handleResize = (): void => {
  //     if (window.innerWidth < 1024 || window.innerHeight < 768) {
  //       if (
  //         videoRef.current?.getInternalPlayer()?.getPlayerState() ===
  //         YoutubePlayerState.PLAYING
  //       ) {
  //         videoRef.current?.getInternalPlayer()?.pauseVideo()
  //       }
  //       if (gameState !== GameStates.ATTRACT) {
  //         setGameState(GameStates.PAUSED)
  //       }
  //       pauseMusic()
  //     }
  //   }

  //   window.addEventListener('resize', handleResize)

  //   // Cleanup on unmount
  //   return () => {
  //     window.removeEventListener('resize', handleResize)
  //   }
  // }, [])

  useEffect(() => {
    if (hasInteracted) {
      if (
        episode?.introMusic?.source === 'youtube' ||
        episode?.introMusic?.source === 'current'
      ) {
        playIntroMusic()
      }
    }
  }, [hasInteracted])

  useEffect(() => {
    initialRoomSettings.options.playImport.value = handleImportClick
    setRoomSettings(initialRoomSettings)
    const handlePopState = (): void => {
      setCurrentEpisodeSlug(
        window.location.pathname.split('/playalong/')[1] ?? ''
      )
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  useEffect(() => {
    if (currentEpisodeSlug !== '') {
      if (currentEpisodeSlug === 'import') {
        document.title = 'Playalong: Import Episode'
        return
      }
      if (currentEpisodeSlug === 'debug') {
        document.title = 'Playalong: Debug Episode'
        handleDebugEpisode()
        return
      }
      if (episodes[currentEpisodeSlug] === undefined) {
        setCurrentEpisodeSlug('')
        document.title = title({ routeParams: { id: '' } })
        window.history.pushState({}, '', '/playalong/')
      } else {
        window.history.pushState({}, '', `/playalong/${currentEpisodeSlug}`)
        document.title = title({ routeParams: { id: currentEpisodeSlug } })
        setEpisode(episodes[currentEpisodeSlug])
        setGameState(GameStates.LOADING)
      }
    } else {
      setEpisode(episodes[currentEpisodeSlug])
      document.title = title({ routeParams: { id: '' } })
      setGameState(GameStates.ATTRACT)
      if (document.location.pathname !== '/playalong/') {
        window.history.pushState({}, '', '/playalong/')
      }
    }
  }, [currentEpisodeSlug])

  useEffect(() => {
    if (!isConnecting && roomSettings !== null) {
      isConnecting = true

      // if (isDevMode) {      }
      connection('pa', PlayAlongOutCallback, { triggerAction, triggerEnter })
    }
    updateMusicVolume()
  }, [roomSettings])

  useEffect(() => {
    updateTime()
    switch (gameState) {
      case GameStates.ATTRACT:
        clearInterval(gameVars.current.stateInterval)
        audioRef.current.theme?.stop()
        break
      case GameStates.LOADING:
        if (episode === undefined) {
          return
        }
        setupMusic()

        clearInterval(gameVars.current.stateInterval)
        gameVars.current.upcomingEvents = episode.events
        gameVars.current.stateInterval = setInterval(LoadingState, 500)
        break

      case GameStates.SETUP:
        videoRef.current
          .getInternalPlayer()
          .setVolume(roomSettings.options.videoVolume.value)
        videoRef.current
          .getInternalPlayer()
          .seekTo(episodes[currentEpisodeSlug].startTime)
        videoRef.current.getInternalPlayer().pauseVideo()
        playIntroMusic()

        clearInterval(gameVars.current.stateInterval)
        break
      case GameStates.PLAYING:
        pauseMusic()
        setShowPrompt(false)
        setShowResults(false)
        setShowScores(false)
        setShowAnswers(false)
        sendPlayersWaiting()
        clearInterval(gameVars.current.stateInterval)
        videoRef.current.getInternalPlayer().playVideo()
        setTimeout(() => {
          pauseMusic()
        }, 50)
        gameVars.current.stateInterval = setInterval(PlayingState, 10)
        break
      case GameStates.ANSWER:
        audioRef.current.prompt?.stop()
        setShowAnswers(true)
        clearInterval(gameVars.current.stateInterval)
        setTieredAnswerIndex(-1)
        gameVars.current.stateInterval = setInterval(AnswerState, 50)
        break
      case GameStates.RESULTS:
        audioRef.current.prompt?.stop()
        setShowPrompt(false)
        setShowResults(true)
        clearInterval(gameVars.current.stateInterval)
        gameVars.current.stateInterval = setInterval(ResultsState, 50)
        break
      case GameStates.SCORES:
        audioRef.current.prompt?.stop()
        setPrevQuickResults([])
        setShowResults(false)
        setShowScores(true)
        clearInterval(gameVars.current.stateInterval)
        gameVars.current.stateInterval = setInterval(ScoresState, 50)
        break
      case GameStates.PAUSED:
        videoRef.current.getInternalPlayer().pauseVideo()
        clearInterval(gameVars.current.stateInterval)
        gameVars.current.stateInterval = setInterval(PausedState, 50)
        break
      case GameStates.LIVE_PROMPT:
        pauseMusic()
        clearInterval(gameVars.current.stateInterval)
        // triggerEvent()
        setShowPrompt(true)
        setTimeout(
          () => {
            sendPlayersPrompt()
          },
          roomSettings?.options?.promptDurationDelay.value * 1000
        )
        gameVars.current.stateInterval = setInterval(LivePromptState, 50)
        break
      case GameStates.LIVE_PAUSED:
        pauseMusic()
        clearInterval(gameVars.current.stateInterval)
        playPromptMusic()
        setShowPrompt(true)
        gameVars.current.stateInterval = setInterval(LivePausedState, 50)
        break
      case GameStates.PAUSE_PROMPT:
        pauseMusic()
        playPromptMusic()
        videoRef.current.getInternalPlayer().pauseVideo()
        clearInterval(gameVars.current.stateInterval)
        // triggerEvent()
        setShowPrompt(true)
        setTimeout(
          () => {
            sendPlayersPrompt()
          },
          roomSettings?.options?.promptDurationDelay.value * 1000
        )
        setTimeout(() => {
          gameVars.current.stateInterval = setInterval(PausedPromptState, 50)
        }, 50)
        break
      case GameStates.END:
        clearInterval(gameVars.current.stateInterval)
        setShowAnswers(false)
        setShowPrompt(false)
        setShowResults(false)
        setShowScores(true)
        sendPlayersEnd()
        break
    }
    gameStateRef.current = gameState
    setIsPaused(
      [
        GameStates.PAUSED,
        GameStates.LIVE_PAUSED,
        GameStates.PAUSE_PROMPT,
        GameStates.SETUP
      ].includes(gameState)
    )
  }, [gameState])

  useEffect(() => {
    currentResultsRef.current = currentResults
  }, [currentResults])

  const sendPlayersPrompt = (): void => {
    CHANNEL_GLOBAL?.trigger('client-message_sent', {
      id: CHANNEL_GLOBAL.members.myID,
      action: 'guessing',
      value:
        gameVars.current.currentEvent?.stringQuestion ??
        gameVars.current.currentEvent?.question,
      choices:
        gameVars.current.currentEvent?.type === 'multiple'
          ? gameVars.current.currentEvent?.choices ?? null
          : null,
      clientId: CHANNEL_GLOBAL.members.me.info.name,
      creator: episode?.series.creator
    })
  }

  const sendPlayersWaiting = (): void => {
    CHANNEL_GLOBAL?.trigger('client-message_sent', {
      id: CHANNEL_GLOBAL.members.myID,
      action: 'waiting',
      value: playerScores,
      clientId: CHANNEL_GLOBAL.members.me.info.name,
      creator: episode?.series.creator
    })
  }

  const sendPlayersScore = (): void => {
    CHANNEL_GLOBAL?.trigger('client-message_sent', {
      id: CHANNEL_GLOBAL.members.myID,
      action: 'score',
      value: playerScores,
      clientId: CHANNEL_GLOBAL.members.me.info.name,
      creator: episode?.series.creator
    })
  }

  const sendPlayersEnd = (): void => {
    CHANNEL_GLOBAL?.trigger('client-message_sent', {
      id: CHANNEL_GLOBAL.members.myID,
      action: 'end',
      value: playerScores,
      clientId: CHANNEL_GLOBAL.members.me.info.name,
      creator: episode?.series.creator
    })
  }

  const LoadingState = (): void => {
    if (
      typeof videoRef.current?.getInternalPlayer()?.getCurrentTime !==
      'undefined'
    ) {
      setGameState(GameStates.SETUP)
    }
  }

  const PlayingState = (): void => {
    updateTime()
    if (episode === undefined) {
      setGameState(GameStates.ATTRACT)
      return
    }
    if (
      gameVars.current.currentTime >=
      gameVars.current.upcomingEvents[0].startTime
    ) {
      triggerEvent()
    }
    if (gameVars.current.currentTime >= episode.endTime - 1) {
      setGameState(GameStates.END)
    }
    if (
      videoRef.current?.getInternalPlayer()?.getPlayerState() ===
      YoutubePlayerState.PAUSED
    ) {
      setGameState(GameStates.PAUSED)
    }
  }

  const PausedState = (): void => {
    updateTime()
    if (
      videoRef.current.getInternalPlayer().getPlayerState() ===
      YoutubePlayerState.PLAYING
    ) {
      setGameState(GameStates.PLAYING)
    }
  }

  const LivePromptState = (): void => {
    updateTime()
    if (episode === undefined) {
      setGameState(GameStates.ATTRACT)
      return
    }
    if (
      videoRef.current.getInternalPlayer().getPlayerState() ===
      YoutubePlayerState.PAUSED
    ) {
      setGameState(GameStates.LIVE_PAUSED)
    }

    if (gameVars.current.currentTime >= episode.endTime) {
      setGameState(GameStates.END)
    }

    if (
      gameVars.current.currentEvent?.endTime !== undefined &&
      gameVars.current.currentTime >= gameVars.current.currentEvent?.endTime
    ) {
      if (
        gameVars.current.currentEvent?.pause === true ||
        roomSettings.options.promptControl.value === 'automated'
      ) {
        setGameState(GameStates.PAUSE_PROMPT)
      } else {
        clearEvent()
        setShowPrompt(false)
        setGameState(GameStates.PLAYING)
      }
    }
  }

  const LivePausedState = (): void => {
    if (
      videoRef.current.getInternalPlayer().getPlayerState() ===
      YoutubePlayerState.PLAYING
    ) {
      setGameState(GameStates.LIVE_PROMPT)
    }
  }

  const PausedPromptState = (): void => {
    if (
      videoRef.current.getInternalPlayer().getPlayerState() ===
      YoutubePlayerState.PLAYING
    ) {
      clearEvent()
      setShowPrompt(false)
      setGameState(GameStates.PLAYING)
    }
  }

  const AnswerState = (): void => {
    if (
      ['results', 'quickResults', 'tieredResults'].includes(
        gameVars.current.currentEvent?.type ?? ''
      )
    ) {
      updateTime()
      const currentTime = Math.ceil(gameVars.current.currentTime)
      const currentEvent = gameVars.current.currentEvent
      if (currentEvent?.type === 'tieredResults') {
        let prevTime = 0
        let tempIndex = -1
        currentEvent.tieredResults?.forEach((time, index) => {
          if (time >= prevTime && currentTime >= time) {
            prevTime = time
            tempIndex = index
          }
        })
        if (tempIndex !== -1) {
          setTieredAnswerIndex(tempIndex)
        }
      }
      if (
        currentEvent !== undefined &&
        (currentEvent?.type !== 'tieredResults'
          ? currentTime >= currentEvent.startTime + ANSWER_TIME
          : currentTime >=
            (currentEvent.endTime ?? 0) - RESULTS_TIME - SCORES_TIME)
      ) {
        setShowAnswers(false)
        updateScores()
        sendPlayersScore()
        switch (currentEvent.type) {
          case 'results':
          case 'tieredResults':
            setGameState(GameStates.RESULTS)
            break
          case 'quickResults':
            setPrevQuickResults([...prevQuickResults, currentEvent.id])
            clearEvent()
            setGameState(GameStates.RESULTS)
            break
          default:
        }
      }
    } else {
      setGameState(GameStates.PLAYING)
    }
  }

  const ResultsState = (): void => {
    if (
      ['results', 'tieredResults'].includes(
        gameVars.current.currentEvent?.type ?? ''
      )
    ) {
      updateTime()
      const currentTime = Math.ceil(gameVars.current.currentTime)
      if (gameVars.current.currentEvent?.type === 'tieredResults') {
        if (
          currentTime >=
          (gameVars.current.currentEvent.endTime ?? 0) - SCORES_TIME
        ) {
          setGameState(GameStates.SCORES)
        }
      } else if (
        gameVars.current.currentEvent !== undefined &&
        currentTime >=
          gameVars.current.currentEvent.startTime + ANSWER_TIME + RESULTS_TIME
      ) {
        setShowResults(false)
        setGameState(GameStates.SCORES)
      }
    } else {
      setGameState(GameStates.PLAYING)
    }
  }

  const ScoresState = (): void => {
    if (
      ['results', 'scores', 'tieredResults'].includes(
        gameVars.current.currentEvent?.type ?? ''
      )
    ) {
      updateTime()
      const currentTime = Math.ceil(gameVars.current.currentTime)
      if (gameVars.current.currentEvent?.type === 'tieredResults') {
        if (currentTime >= (gameVars.current.currentEvent.endTime ?? 0)) {
          setGameState(GameStates.PLAYING)
          clearEvent()
        }
      } else if (
        gameVars.current.currentEvent !== undefined &&
        currentTime >=
          gameVars.current.currentEvent.startTime +
            ANSWER_TIME +
            RESULTS_TIME +
            SCORES_TIME
      ) {
        setGameState(GameStates.PLAYING)
        clearEvent()
      }
    } else {
      setGameState(GameStates.PLAYING)
    }
  }

  const triggerEvent = (): void => {
    if (gameVars.current.currentEvent === undefined) {
      const tempEvent = gameVars.current.upcomingEvents.shift()
      if (tempEvent !== undefined) {
        gameVars.current.previousEvents[tempEvent.id] = {
          event: tempEvent,
          result: {}
        }

        if (
          ['results', 'quickResults', 'tieredResults'].includes(tempEvent.type)
        ) {
          setCurrentResults(tempEvent.resultsFor ?? '0')
          setGameState(GameStates.ANSWER)
        } else if (tempEvent.type === 'scores') {
          setGameState(GameStates.SCORES)
        } else if (tempEvent.type === 'finalResults') {
          setGameState(GameStates.END)
        } else if (tempEvent.endTime !== undefined) {
          setGameState(GameStates.LIVE_PROMPT)
        } else {
          setGameState(GameStates.PAUSE_PROMPT)
        }
        gameVars.current.currentEvent = tempEvent
      }
    }
  }

  const clearEvent = (): void => {
    console.log('clearing event')
    gameVars.current.currentEvent = undefined
  }

  const updateTime = (): void => {
    if (
      typeof videoRef.current?.getInternalPlayer()?.getCurrentTime !==
      'undefined'
    ) {
      gameVars.current.currentTime =
        videoRef.current?.getInternalPlayer()?.getCurrentTime() ?? 0
    } else {
      gameVars.current.currentTime = 0
    }

    setTotalSeconds(Math.floor(gameVars.current.currentTime))
  }

  const calculateScore = (event: Event, guess: string): number => {
    if (event.points === undefined) {
      event.points = 0
    }
    if (event.type === 'tieredAnswers') {
      const threshold = event.threshold ?? 0.8
      let tempScore = 0
      event.tieredAnswers?.forEach((answer) => {
        const acceptableAnswers = answer.acceptableAnswers as string[]
        let correctAnswer
        let correctAnswerPercentage = 0
        acceptableAnswers.forEach((answer) => {
          const similarity = stringSimilarity(guess, answer, 1)
          if (similarity > threshold && similarity > correctAnswerPercentage) {
            correctAnswer = answer
            correctAnswerPercentage = similarity
          }
        })
        if (correctAnswer !== undefined) {
          const difference = threshold - correctAnswerPercentage
          if (difference <= 0) {
            if (answer.points > tempScore) {
              tempScore = answer.points
            }
          }
        }
      })
      return tempScore
    }
    if (event.type === 'numberCurve') {
      const guessNumber = parseInt(guess)
      const answer = event.answer as number
      const difference = Math.abs(guessNumber - answer)
      const stepAmount = event.stepAmount ?? 1
      const stepDrop = event.stepDrop ?? 1
      const score = Math.max(
        0,
        event.points - Math.floor(difference / stepAmount) * stepDrop
      )
      return score
    } else if (event.type === 'percentageCurve') {
      let guessPercentage
      if (guess.includes('%')) {
        guessPercentage = parseFloat(guess) / 100
      } else {
        guessPercentage = parseFloat(guess)
      }

      const answerPercentage = event.answer as number
      const percentageDifference = Math.abs(guessPercentage - answerPercentage)
      const stepAmount = event.stepAmount ?? 1
      const stepDrop = event.stepDrop ?? 1
      const percentageScore = Math.max(
        0,
        event.points - Math.floor(percentageDifference / stepAmount) * stepDrop
      )
      return percentageScore
    } else if (event.type === 'stringCompare') {
      const acceptableAnswers = event.acceptableAnswers as string[]
      const threshold = event.threshold ?? 0.8
      const stepAmount = event.stepAmount ?? 0.05
      const stepDrop = event.stepDrop ?? 1
      let correctAnswer
      let correctAnswerPercentage = 0
      acceptableAnswers.forEach((answer) => {
        const similarity = stringSimilarity(guess, answer, 1)
        if (similarity > threshold && similarity > correctAnswerPercentage) {
          correctAnswer = answer
          correctAnswerPercentage = similarity
        }
      })
      if (correctAnswer !== undefined) {
        const difference = threshold - correctAnswerPercentage
        if (difference <= 0) {
          return event.points
        }
        const score = Math.max(
          0,
          event.points - Math.floor(difference / stepAmount) * stepDrop
        )
        return score
      }
      return 0
    } else if (event.type === 'multiple') {
      const answer = alphaToNumeric(guess)
      if (answer === event.answer) {
        return event.points ?? 0
      }
      if (event.acceptableAnswers?.includes(answer) ?? false) {
        return event.points ?? 0
      }
      if (parseInt(guess) === answer) {
        return event.points ?? 0
      }
      return 0
    } else if (event.type === 'order') {
      const answer = event.answer as number
      if (
        guess.toLocaleLowerCase().trim() ===
        answer.toString().toLocaleLowerCase()
      ) {
        return event.points ?? 0
      }
      return 0
    }
    return 0
  }

  const updateScores = (): void => {
    const results = gameVars.current.previousEvents[currentResultsRef.current]
    const scores = playerScores
    Object.entries(results.result).forEach(([username, result]) => {
      let player = scores.find((player) => player.username === username)

      if (player === undefined) {
        player = {
          username,
          score: 0
        }
        scores.push(player)
      }

      player.score += result.score
    })
    const sortedScores = Object.values(scores).sort((a, b) => b.score - a.score)

    setPlayerScores([...sortedScores])
  }

  const backToMenuButton = (): void => {
    resetRoom()
  }
  const playButton = (): void => {
    if (
      videoRef.current.getInternalPlayer().getPlayerState() ===
      YoutubePlayerState.PLAYING
    ) {
      videoRef.current.getInternalPlayer().pauseVideo()
      setIsPaused(true)
    } else {
      videoRef.current.getInternalPlayer().playVideo()
      setIsPaused(false)
      if (gameState === GameStates.SETUP) {
        if (
          episode?.promptMusic?.url !== undefined &&
          episode?.promptMusic?.url !== '' &&
          episode?.promptMusic?.source !== 'mp3' &&
          episode?.promptMusic?.source !== 'none'
        ) {
          setCurrentMusicUrl(episode?.promptMusic?.url ?? '')
        }
        setGameState(GameStates.PLAYING)
      }
    }
  }

  const rewindButton = (): void => {
    if (gameVars.current.currentTime > 30) {
      gameVars.current.currentTime -= 30
      videoRef.current.getInternalPlayer().seekTo(gameVars.current.currentTime)
    } else {
      videoRef.current.getInternalPlayer().seekTo(0)
    }
  }

  const nextTieredResult = (): number => {
    const currentEvent = gameVars.current.currentEvent
    const currentTime = Math.ceil(gameVars.current.currentTime)
    let tempIndex = -1
    let nextTime = Infinity
    currentEvent?.tieredResults?.forEach((time, index) => {
      if (time >= currentTime && time < nextTime) {
        nextTime = time
        tempIndex = index
      }
    })
    return tempIndex
  }

  const forwardButton = (): void => {
    const currentEvent = gameVars.current.currentEvent

    if (
      (gameState === GameStates.ANSWER &&
        currentEvent?.type !== 'tieredResults') ||
      (gameVars.current.upcomingEvents[0]?.startTime - NEXT_EVENT_BUFFER_TIME <
        gameVars.current.currentTime &&
        currentEvent?.type !== 'tieredResults')
    ) {
      return
    }

    if (
      currentEvent?.type === 'tieredResults' &&
      currentEvent?.tieredResults !== undefined
    ) {
      const tempIndex = nextTieredResult()
      if (tempIndex !== -1) {
        // setTieredAnswerIndex(tempIndex)
        videoRef.current
          .getInternalPlayer()
          .seekTo(
            currentEvent?.tieredResults[tempIndex] - NEXT_EVENT_BUFFER_TIME
          )
        return
      }
    }

    if (gameVars.current.upcomingEvents.length >= 1) {
      clearEvent()
      setGameState(GameStates.PLAYING)
      if (currentEvent?.type === 'results') {
        setPrevQuickResults([])
      }
      videoRef.current
        .getInternalPlayer()
        .seekTo(
          gameVars.current.upcomingEvents[0].startTime - NEXT_EVENT_BUFFER_TIME
        )
    }
  }

  const currentEvent: Event | undefined = gameVars.current.currentEvent
  const upcomingEvents: Event[] = gameVars.current.upcomingEvents

  const previousResults = JSON.parse(
    JSON.stringify(gameVars.current.previousEvents[currentResults] ?? {})
  )
  if (prevQuickResults.length > 0 && gameState === GameStates.RESULTS) {
    prevQuickResults.forEach((id) => {
      if (gameVars.current.previousEvents[id].event.resultsFor === undefined) {
        return
      }
      const prevQuickResult =
        gameVars.current.previousEvents[
          gameVars.current.previousEvents[id].event.resultsFor
        ].result
      Object.entries(prevQuickResult).forEach(
        ([username, player]: [
          string,
          { answer: string | number; score: number }
        ]) => {
          if (previousResults.result[username] === undefined) {
            previousResults.result[username] = { answer: '', score: 0 }
          }
          previousResults.result[username].score =
            parseInt(previousResults.result[username].score) + player.score
        }
      )
    })
  }
  // debugger
  const tieredIndex = nextTieredResult()
  const canForward =
    (gameState === GameStates.ANSWER &&
      currentEvent?.type === 'tieredResults' &&
      tieredIndex !== -1 &&
      (currentEvent?.tieredResults?.[tieredIndex] ?? 0) -
        NEXT_EVENT_BUFFER_TIME >
        gameVars.current.currentTime) ||
    (gameState !== GameStates.ANSWER &&
      upcomingEvents[0]?.startTime - NEXT_EVENT_BUFFER_TIME >
        gameVars.current.currentTime)

  return (
    <>
      <input
        type="file"
        accept=".json"
        onChange={handleImportFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }} // Hide the input
      />
      <Box
        sx={
          gameState === GameStates.ATTRACT ? styles.attract : styles.container
        }
      >
        {gameState === GameStates.ATTRACT && (
          <EpisodeList setCurrentEpisodeSlug={setCurrentEpisodeSlug} />
        )}
        <Box display={gameState !== GameStates.ATTRACT ? 'contents' : 'none'}>
          <Box
            as="iframe"
            pos="absolute"
            inset={{
              base: `calc(Min(${mobileMaxVideoHeight}, 100vw * 9 / 16)) 0 0 0`,
              lg: '45px 10px auto auto'
            }}
            display={{
              base: 'block',
              lg:
                roomSettings?.options?.splitScreen.value === true
                  ? 'block'
                  : 'none'
            }}
            w={{ base: '100%', lg: '320px', '2xl': '400px' }}
            h={{
              base: `calc(100dvh - 59px - calc(Min(${mobileMaxVideoHeight}, 100vw * 9 / 16)))`,
              lg: '480px',
              '2xl': '560px'
            }}
            src={`/join?room-code=${roomCode}&mobile-no-nav=true`}
          />

          <Button
            display={{
              base: 'none',
              lg: 'block'
            }}
            borderBottomLeftRadius="0"
            borderBottomRightRadius="0"
            className={
              roomSettings?.options?.splitScreen.value === true
                ? 'show'
                : 'hide'
            }
            position="absolute"
            zIndex={1300}
            backgroundColor="yellow.400"
            color="gray.700"
            // display={roomSettings?.options?.splitScreen.value === false ? 'block' : 'none'}
            transition="none"
            sx={{
              '&.show': {
                display: 'block',
                right: '10px',
                top: '5px'
              },
              '&.hide': {
                right: '-85px',
                top: '87px',
                transform: 'rotate(-90deg)'
              }
            }}
            onClick={(): void => {
              setRoomSettings({
                ...roomSettings,
                options: {
                  ...roomSettings.options,
                  splitScreen: {
                    ...roomSettings.options.splitScreen,
                    value: !(roomSettings.options.splitScreen.value as boolean)
                  }
                }
              })
            }}
          >
            {roomSettings?.options?.splitScreen.value === false ? (
              <>Show</>
            ) : (
              <>Hide</>
            )}{' '}
            Guessing Screen
          </Button>

          <Center
            p={{
              base: 0,
              lg: '20px'
            }}
            pos="absolute"
            inset={
              roomSettings?.options?.splitScreen?.value === true
                ? '20px 360px 80px 260px'
                : {
                    base: '0 0 auto 0',
                    lg: '20px 0px 80px 360px',
                    '2xl': '20px 0px 80px 360px'
                  }
            }
          >
            <Box
              aspectRatio={16 / 9}
              w="100%"
              maxWidth={{
                base: '100%',
                lg: 'calc(calc(100dvh - 200px) * (16/9))'
              }}
              maxHeight={{
                base: 'none',
                sm: mobileMaxVideoHeight,
                lg: 'none'
              }}
              pos="relative"
              m={{ base: '0', lg: '0 0px 0 30px', '2xl': '60px' }}
            >
              {episode !== undefined && (
                <Box
                  pos="absolute"
                  inset="0"
                  overflow="hidden"
                  sx={{
                    '.react-player': {
                      borderRadius: '20px',
                      position: 'absolute',
                      top: '50%',
                      height: '100%',
                      minWidth: '100%',
                      minHeight: '100%',
                      transform: 'translateY(-50%)',
                      aspectRatio: [
                        GameStates.LIVE_PAUSED,
                        GameStates.PAUSE_PROMPT,
                        GameStates.PAUSED
                      ].includes(gameStateRef.current)
                        ? 1
                        : 16 / 9
                    }
                  }}
                >
                  <Box
                    as={ReactPlayer}
                    ref={videoRef}
                    className="react-player"
                    url={episode.videoUrl}
                    width="100%"
                    minWidth="100%"
                    maxWidth="100%"
                    height="auto !important"
                    maxHeight={{
                      base: mobileMaxVideoHeight,
                      lg: '100%'
                    }}
                    overflow="hidden"
                    onReady={(): void => {
                      videoRef.current
                        ?.getInternalPlayer()
                        ?.setVolume(roomSettings.options.videoVolume.value)
                    }}
                    onEnded={(): void => {
                      if (gameState === GameStates.END) {
                        playIntroMusic()
                      }
                    }}
                    style={{
                      borderRadius: '20px'
                    }}
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 0,
                          disablekb: 1,
                          cc_load_policy: 1,
                          fs: 0,
                          rel: 0,
                          showinfo: 0,
                          widget_referrer: 'https://www.reactvts.com'
                        }
                      }
                    }}
                  />
                  <Box
                    as={ReactPlayer}
                    ref={musicRef}
                    url={currentMusicUrl}
                    overflow="hidden"
                    playing={false}
                    onReady={(): void => {
                      musicRef.current?.getInternalPlayer()?.setVolume(0)
                    }}
                    onPlay={(): void => {
                      fadeMusic(0, roomSettings.options.musicVolume.value, 1000)
                    }}
                    onPause={(): void => {
                      musicRef.current?.getInternalPlayer()?.setVolume(0)
                    }}
                    onEnded={(): void => {
                      musicRef.current?.getInternalPlayer()?.setVolume(0)
                      if (gameState === GameStates.SETUP) {
                        playIntroMusic()
                      } else {
                        playPromptMusic()
                      }
                    }}
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 1,
                          disablekb: 1,
                          cc_load_policy: 1,
                          fs: 0,
                          rel: 0,
                          showinfo: 0,
                          widget_referrer: 'https://www.reactvts.com',
                          volume: roomSettings.options.musicVolume.value
                        }
                      }
                    }}
                  />
                </Box>
              )}
              <Box inset="0" pos="absolute" />
              {episode?.videoPreview !== undefined &&
                [GameStates.LOADING, GameStates.SETUP].includes(gameState) && (
                  <Img
                    backgroundColor="#181425"
                    aspectRatio={16 / 9}
                    src={episode.videoPreview}
                    inset={0}
                    pos="absolute"
                    objectFit={'contain'}
                    width="100%"
                    height="100%"
                    maxHeight={{
                      base: 'none',
                      sm: mobileMaxVideoHeight,
                      lg: 'none'
                    }}
                    overflow="hidden"
                    // borderRadius="20px"
                  />
                )}
              <ButtonGroup
                pos="absolute"
                left="0px"
                bottom={{
                  base: '-18px',
                  lg: '-60px'
                }}
                w="100%"
                display="flex"
                justifyContent="center"
              >
                {gameState === GameStates.SETUP && (
                  <CircleButton
                    icon={isPaused ? MonitorPlay : MonitorPause}
                    showHover={true}
                    hoverWidth="9.72em"
                    label="START SHOW"
                    zIndex={10}
                    onClick={playButton}
                  />
                )}
                {gameState !== GameStates.LOADING &&
                  gameState !== GameStates.SETUP && (
                    <CircleButton
                      icon={isPaused ? MonitorPlay : MonitorPause}
                      showHover={gameState === GameStates.PAUSE_PROMPT}
                      hoverWidth={
                        isPaused
                          ? gameState === GameStates.PAUSE_PROMPT
                            ? '11.8em'
                            : '6.11em'
                          : '6.38em'
                      }
                      label={
                        isPaused
                          ? gameState === GameStates.PAUSE_PROMPT
                            ? 'FINISH GUESSING'
                            : 'PLAY'
                          : 'PAUSE'
                      }
                      zIndex={10}
                      onClick={playButton}
                    />
                  )}
                {![
                  GameStates.PAUSE_PROMPT,
                  GameStates.LOADING,
                  GameStates.SETUP
                ].includes(gameState) && (
                  <CircleButton
                    icon={History}
                    hoverWidth="8.75em"
                    label="REWIND 30"
                    onClick={rewindButton}
                  />
                )}
                {![
                  GameStates.PAUSE_PROMPT,
                  GameStates.LOADING,
                  GameStates.SETUP
                ].includes(gameState) && (
                  <CircleButton
                    opacity={canForward ? 1 : 0.15}
                    cursor={canForward ? 'pointer' : 'not-allowed'}
                    icon={SkipForward}
                    hoverWidth="11.5em"
                    label={
                      nextTieredResult() !== -1 ||
                      (upcomingEvents.length > 0 &&
                        upcomingEvents[0].type.includes('esults')) // quickResult or resultss
                        ? 'SEE RESULTS'
                        : 'NEXT QUESTION'
                    }
                    onClick={forwardButton}
                  />
                )}
              </ButtonGroup>
            </Box>
          </Center>
          <AnimatePresence>
            {showScores && (
              <Box display={{ base: 'none', lg: 'contents' }}>
                <Box
                  key="score-card"
                  as={motion.div}
                  variants={stageMotion}
                  w={{ base: '320px', '2xl': '420px' }}
                  pos="absolute"
                  inset="40px auto 40px 0"
                  initial="hiddenScore"
                  animate="visible"
                  exit="hiddenScore"
                  bg="white"
                  p="20px"
                  // borderTopRightRadius="12px"
                  // borderBottomRightRadius="12px"
                  background="yellow.300"
                  overflow="hidden"
                  boxShadow="md"
                >
                  <Heading textAlign="center">
                    {gameState !== GameStates.END ? 'Current' : 'Final'} Scores
                  </Heading>
                  <Grid
                    w="100%"
                    templateColumns="1fr 1fr"
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      '& .th': {
                        bg: 'gray.700',
                        color: 'white',
                        textAlign: 'center'
                      },
                      '& .tr': {
                        px: '5px',
                        '&.score': {
                          textAlign: 'center'
                        }
                      },
                      '& .row-even': {
                        bg: 'rgba(0,0,0,0.05)'
                      },
                      '& .row-odd': {
                        bg: 'rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <>
                      <GridItem className="th">Player</GridItem>
                      <GridItem className="th">Score</GridItem>
                    </>
                    {playerScores.map((player, index) => {
                      return (
                        <React.Fragment key={`player-${index}`}>
                          <GridItem
                            className={`tr row-${
                              (index + 1) % 2 === 0 ? 'even' : 'odd'
                            } name`}
                          >
                            {player.username}
                          </GridItem>
                          <GridItem
                            className={`tr row-${
                              (index + 1) % 2 === 0 ? 'even' : 'odd'
                            } score`}
                          >
                            {player.score}
                          </GridItem>
                        </React.Fragment>
                      )
                    })}
                  </Grid>
                  {gameState === GameStates.END && (
                    <Text textAlign="center" w="100%" mt="20px">
                      <Button
                        variant="outlineNegative"
                        onClick={backToMenuButton}
                      >
                        Back to Episodes List
                      </Button>
                    </Text>
                  )}
                </Box>
              </Box>
            )}

            {/* Setup card */}

            {gameState === GameStates.SETUP && episode !== undefined && (
              <Box display={{ base: 'none', lg: 'contents' }}>
                <Box
                  key="setup-card"
                  as={motion.div}
                  variants={stageMotion}
                  w={{ base: '320px', '2xl': '420px' }}
                  pos="absolute"
                  inset="50% auto auto 0"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  bg="white"
                  p="20px"
                  // borderTopRightRadius="12px"
                  // borderBottomRightRadius="12px"
                  background="yellow.300"
                  overflow="hidden"
                  boxShadow="md"
                  display={{ base: 'none', lg: 'block' }}
                  sx={{
                    '.chakra-heading': {
                      fontSize: { base: '24px', '2xl': '32px' }
                    },
                    '.chakra-text': {
                      fontSize: { base: '16px', '2xl': '24px' }
                    },
                    '.playalong-title': {
                      fontWeight: 800,
                      fontSize: { base: '18px', '2xl': '24px' },
                      textTransform: 'uppercase',
                      mb: '15px',
                      span: {
                        fontWeight: 400,
                        textTransform: 'none'
                      }
                    }
                  }}
                >
                  <Heading
                    as="div"
                    textAlign="center"
                    lineHeight="0.9"
                    className="playalong-title"
                  >
                    Playalong <span>Presents:</span>
                  </Heading>
                  <Heading
                    as="div"
                    textAlign="center"
                    lineHeight="0.9"
                    sx={{
                      textWrap: 'balance'
                    }}
                  >
                    {episode.title}
                  </Heading>
                  <Flex gap="10px" justifyContent="center">
                    <Img
                      src={episode.series.creator?.channelIcon}
                      w="50vw"
                      h="50vw"
                      maxW="50px"
                      maxH="50px"
                      borderRadius="50px"
                      boxShadow={'0px 0px 3px 2px yellow.400'}
                      alt={episode.series.creator?.name}
                      mb="10px"
                    />
                    <Text
                      as="a"
                      textAlign="left"
                      href={episode.series.creator.qrCode}
                      display="inline-block"
                      mb="0"
                      fontSize="0.8"
                      lineHeight="1"
                      fontWeight={400}
                      target="_blank"
                      sx={{
                        textWrap: 'balance'
                      }}
                    >
                      <strong>{episode.series.name}</strong>
                      <Box whiteSpace="nowrap">
                        by {episode.series.creator.name}
                        <Box
                          as={ExternalLink}
                          verticalAlign="text-top"
                          h="1em"
                          pt="3px"
                          ml="0.25em"
                          mt="1px"
                          display="inline"
                        />
                      </Box>
                    </Text>
                  </Flex>
                  <Box w="100%" backgroundColor="gray.700" h="1px" my="20px" />
                  <Heading textAlign="center" mb="5px">
                    How to Play
                  </Heading>
                  <Text fontSize="0.8em" lineHeight="1.25">
                    Go to <strong>Reactvts.com/join</strong> and follow the
                    on-screen instructions to join. Click{' '}
                    <Box
                      display="inline-block"
                      w="1.5em"
                      border="1px solid black"
                      // h="20px"
                      as="button"
                      fontSize="0.8em"
                      aspectRatio={1}
                      onClick={() => {
                        ;(window as any)?.openDrawer?.toggle()
                      }}
                    >
                      ☰
                    </Box>{' '}
                    for options like stream delay, volume and more.
                  </Text>
                  <Text textAlign="center" my="5px">
                    <strong> Are You Streaming?</strong>
                  </Text>
                  <Text fontSize="0.8em" lineHeight="1.25">
                    If you are streaming on Twitch or YouTube and have a stream
                    delay, click{' '}
                    <Box
                      display="inline-block"
                      w="1.5em"
                      border="1px solid black"
                      // h="20px"
                      as="button"
                      fontSize="0.8em"
                      aspectRatio={1}
                      onClick={() => {
                        ;(window as any)?.openDrawer?.toggle()
                      }}
                    >
                      ☰
                    </Box>{' '}
                    in the top right and set the &ldquo;Guessing Time
                    Delay&rdquo; to match your stream delay.
                  </Text>
                  <Text textAlign="center" w="100%">
                    <Button
                      variant="outlineNegative"
                      onClick={backToMenuButton}
                    >
                      Back to Episodes List
                    </Button>
                  </Text>
                </Box>
              </Box>
            )}
            {/* Mobile Circle Timer */}

            {gameState === GameStates.PAUSE_PROMPT &&
              roomSettings.options.promptControl.value === 'automated' && (
                <Flex
                  display={{ base: 'flex', lg: 'none' }}
                  position="absolute"
                  top="0"
                  left="0"
                  w="100vw"
                  h={`calc(Min(${mobileMaxVideoHeight}, 100vw * 9 / 16))`}
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircleTimer
                    duration={roomSettings.options.promptDuration.value}
                    onComplete={() => {
                      setTimeout(
                        playButton,
                        (1 +
                          parseInt(
                            roomSettings.options.promptDurationDelay.value
                          )) *
                          1000
                      )
                    }}
                    sx={{
                      backgroundColor: 'yellow.400',
                      borderRadius: '50px',
                      width: '100px',
                      height: '100px',
                      color: 'yellow.400',
                      boxShadow: '0px 0px 14px 9px currentColor'
                    }}
                  />
                </Flex>
              )}

            {/* Mobile Setup card */}

            {gameState === GameStates.SETUP && episode !== undefined && (
              <Box display={{ base: 'contents', lg: 'none' }}>
                <Box
                  key="setup-mobile-card"
                  as={motion.div}
                  variants={stageMotion}
                  pos="absolute"
                  inset={`calc(Min(${mobileMaxVideoHeight}, 100vw * 9 / 16)) 0 0 0`}
                  w="100%"
                  h={`calc(100dvh - 59px - calc(Min(${mobileMaxVideoHeight}, 100vw * 9 / 16)))`}
                  overflow="auto"
                  initial="hidden-mobile"
                  animate="visible"
                  exit="hidden-mobile"
                  bg="white"
                  p="20px"
                  borderTop="1.5em solid #181425"
                  // borderTopRightRadius="12px"
                  // borderBottomRightRadius="12px"
                  background="yellow.300"
                  boxShadow="md"
                  sx={{
                    '.chakra-heading': {
                      fontSize: { base: '24px', '2xl': '32px' }
                    },
                    '.chakra-text': {
                      fontSize: { base: '16px', '2xl': '24px' }
                    },
                    '.playalong-title': {
                      fontWeight: 800,
                      fontSize: { base: '18px', '2xl': '24px' },
                      textTransform: 'uppercase',
                      mb: '15px',
                      span: {
                        fontWeight: 400,
                        textTransform: 'none'
                      }
                    }
                  }}
                >
                  <Heading
                    as="div"
                    textAlign="center"
                    lineHeight="0.9"
                    className="playalong-title"
                  >
                    Playalong <span>Presents:</span>
                  </Heading>
                  <Heading
                    as="div"
                    textAlign="center"
                    lineHeight="0.9"
                    sx={{
                      textWrap: 'balance'
                    }}
                  >
                    {episode.title}
                  </Heading>
                  <Flex gap="10px" justifyContent="center">
                    <Img
                      src={episode.series.creator?.channelIcon}
                      w="50vw"
                      h="50vw"
                      maxW="50px"
                      maxH="50px"
                      borderRadius="50px"
                      boxShadow={'0px 0px 3px 2px yellow.400'}
                      alt={episode.series.creator?.name}
                      mb="10px"
                    />
                    <Text
                      as="a"
                      textAlign="left"
                      href={episode.series.creator.qrCode}
                      display="inline-block"
                      mb="0"
                      fontSize="0.8"
                      lineHeight="1"
                      fontWeight={400}
                      target="_blank"
                      sx={{
                        textWrap: 'balance'
                      }}
                    >
                      <strong>{episode.series.name}</strong>
                      <Box whiteSpace="nowrap">
                        by {episode.series.creator.name}
                        <Box
                          as={ExternalLink}
                          verticalAlign="text-top"
                          h="1em"
                          pt="3px"
                          ml="0.25em"
                          mt="1px"
                          display="inline"
                        />
                      </Box>
                    </Text>
                  </Flex>
                  <Box w="100%" backgroundColor="gray.700" h="1px" my="20px" />
                  <Heading textAlign="center" mb="5px">
                    How to Play
                  </Heading>
                  <Flex flexDir="row" gap="50px">
                    <Text fontSize="0.8em" lineHeight="1.25" flex="1">
                      <strong>SOLO PLAY:</strong>
                      <br />
                      Click <strong>&quot;Start Show&quot;</strong> above and
                      follow the on-screen instructions to join.
                    </Text>
                    <Text fontSize="0.8em" lineHeight="1.25" flex="1">
                      <strong>JOIN A GAME:</strong>
                      <br />
                      Click <strong>&quot;Join Game&quot;</strong> at the top of
                      this screen and put in your name and room code
                    </Text>
                  </Flex>
                  <Text fontSize="0.8em" lineHeight="1.25">
                    <strong>Host a Game:</strong>
                    <br />
                    Hosting a Playalong game requires a minimum screen size of
                    1024px by 768px at 100% Zoom
                  </Text>

                  <Text textAlign="center" w="100%">
                    <Button
                      variant="outlineNegative"
                      onClick={backToMenuButton}
                    >
                      Back to Episodes List
                    </Button>
                  </Text>
                </Box>
              </Box>
            )}
            {showPrompt && (
              <Box display={{ base: 'none', lg: 'contents' }}>
                <StickyNote
                  key="sticky-prompt"
                  gap="20px"
                  as={motion.div}
                  variants={stageMotion}
                  w={{ base: '320px', '2xl': '520px' }}
                  pos="absolute"
                  inset="50% auto auto 0"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  justifyContent="space-between"
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text
                      fontWeight={800}
                      fontSize={{ base: '18px', '2xl': '32px' }}
                      textAlign="center"
                      mb="0"
                    >
                      {currentEvent !== null && (
                        <>For {currentEvent?.points} Points:</>
                      )}
                    </Text>
                    {currentEvent?.endTime !== undefined &&
                      currentEvent?.pause === false && (
                        <Box>
                          <CircleTimer
                            duration={
                              currentEvent?.endTime - currentEvent?.startTime
                            }
                          />
                        </Box>
                      )}
                    {gameState === GameStates.PAUSE_PROMPT &&
                      roomSettings.options.promptControl.value ===
                        'automated' && (
                        <Box>
                          <CircleTimer
                            duration={roomSettings.options.promptDuration.value}
                            onComplete={() => {
                              setTimeout(
                                playButton,
                                (1 +
                                  parseInt(
                                    roomSettings.options.promptDurationDelay
                                      .value
                                  )) *
                                  1000
                              )
                            }}
                          />
                        </Box>
                      )}
                  </Flex>
                  <Text
                    fontWeight={800}
                    fontSize={{ base: '18px', '2xl': '32px' }}
                    textAlign="center"
                    sx={{
                      textWrap: 'balance'
                    }}
                    mb="0"
                  >
                    {currentEvent?.question}
                  </Text>
                  {currentEvent?.type === 'multiple' && (
                    <Box p="10px 20px 0">
                      <Text textAlign="center" mt="0px">
                        Enter Your Guess On Your Device
                        {/* <br />
                    <sup>(example: !guess a)</sup> */}
                      </Text>
                      <OptionList
                        options={currentEvent.choices ?? []}
                        chatVote={false}
                      />
                    </Box>
                  )}
                  {currentEvent?.type === 'numberCurve' && (
                    <Text textAlign="center" mt="10px">
                      {/* Type !guess Followed By A Number
                  <br />
                  <sup>(example: !guess 500)</sup>
                  <br /> */}
                      Type In Your Guess On Your Device
                      <br />
                      Close Answers Give Partial Points!
                    </Text>
                  )}
                  {currentEvent?.type === 'percentageCurve' && (
                    <Text textAlign="center" mt="0">
                      Type In Your Guess On Your Device
                      <br />
                      (example: &quot;10%&quot; or &quot;0.1&quot; )
                      <br />
                      Close Answers Give Partial Points!
                    </Text>
                  )}
                  {currentEvent?.type === 'stringCompare' && (
                    <Text textAlign="center" mt="0">
                      Type In Your Guess On Your Device
                      <br />
                      <sup>(Close Answers Give Partial Points!)</sup>
                    </Text>
                  )}
                  {currentEvent?.type === 'tieredAnswers' && (
                    <Text textAlign="center" mt="0">
                      Type In Your Guess On Your Device
                    </Text>
                  )}
                  {currentEvent?.type === 'order' && (
                    <Box p="0 20px">
                      <Text textAlign="center" mt="0px">
                        Type the Numbers In Order
                        <br />
                        <sup>(Example: 2143)</sup>
                      </Text>
                      <OptionList
                        options={currentEvent.choices ?? []}
                        showNumbers={true}
                        chatVote={false}
                      />
                    </Box>
                  )}
                </StickyNote>
              </Box>
            )}
            {showAnswers && previousResults.event.type !== 'tieredAnswers' && (
              <StickyNote
                key="sticky-results"
                as={motion.div}
                variants={stageMotion}
                w={{ base: '320px', '2xl': '420px' }}
                pos="absolute"
                inset="50% auto auto 0"
                initial="hidden"
                animate="visible"
                justifyContent="center"
                exit="hidden"
              >
                <Text fontWeight={900} textAlign="center">
                  {previousResults?.event.question}
                </Text>

                {previousResults?.event.type === 'multiple' &&
                previousResults?.event.displayAnswer === undefined ? (
                  <OptionList
                    options={previousResults.event.choices ?? []}
                    chatVote={false}
                    highlighted={
                      typeof previousResults.event.answer === 'number'
                        ? previousResults.event.answer
                        : -1
                    }
                  />
                ) : (
                  <Text textAlign="center">
                    <strong>Answer</strong>:{' '}
                    {typeof previousResults?.event.answer !== 'object' && (
                      <>
                        {previousResults?.event.displayAnswer ??
                          previousResults?.event.answer}
                      </>
                    )}
                    {typeof previousResults?.event.acceptableAnswers ===
                      'object' &&
                      previousResults?.event.displayAnswer === undefined && (
                        <>{previousResults?.event.acceptableAnswers[0]}</>
                      )}
                  </Text>
                )}

                {typeof previousResults?.event.acceptableAnswers === 'object' &&
                  previousResults?.event.acceptableAnswers.length > 1 &&
                  previousResults?.event.type !== 'multiple' && (
                    <>
                      <Text fontSize="1.2em" textAlign="center">
                        <strong>We also accepted:</strong>
                        <br />
                        &quot;
                        {previousResults?.event.acceptableAnswers
                          .slice(1)
                          .join('", "')}
                        &quot;
                      </Text>
                    </>
                  )}
              </StickyNote>
            )}

            {showAnswers &&
              previousResults.event.type === 'tieredAnswers' &&
              tieredAnswerIndex !== -1 && (
                <StickyNote
                  key={`sticky-results-${tieredAnswerIndex}`}
                  as={motion.div}
                  variants={stageMotion}
                  w={{ base: '320px', '2xl': '420px' }}
                  pos="absolute"
                  inset="50% auto auto 0"
                  initial="hidden"
                  animate="visible"
                  justifyContent="center"
                  exit="hidden"
                >
                  <Text fontWeight={900} textAlign="center">
                    {previousResults?.event.question}
                  </Text>

                  <Text textAlign="center">
                    <strong>Answer</strong>:{' '}
                    {typeof previousResults?.event.tieredAnswers[
                      tieredAnswerIndex
                    ].acceptableAnswers === 'object' && (
                      <>
                        {
                          previousResults?.event.tieredAnswers[
                            tieredAnswerIndex
                          ].acceptableAnswers[0]
                        }{' '}
                        <br />
                        <strong>Points</strong>:{' '}
                        {
                          previousResults?.event.tieredAnswers[
                            tieredAnswerIndex
                          ].points
                        }{' '}
                        points
                      </>
                    )}
                  </Text>

                  {typeof previousResults?.event.tieredAnswers[
                    tieredAnswerIndex
                  ].acceptableAnswers === 'object' &&
                    previousResults?.event.tieredAnswers[tieredAnswerIndex]
                      .acceptableAnswers.length > 1 && (
                      <>
                        <Text fontSize="1.2em" textAlign="center">
                          <strong>We also accepted:</strong>
                          <br />
                          &quot;
                          {previousResults?.event.tieredAnswers[
                            tieredAnswerIndex
                          ].acceptableAnswers
                            .slice(1)
                            .join('", "')}
                          &quot;
                        </Text>
                      </>
                    )}
                </StickyNote>
              )}

            {!(showAnswers || showPrompt || showResults || showScores) &&
              [GameStates.PLAYING, GameStates.PAUSED].includes(gameState) &&
              episode !== undefined && (
                <Box display={{ base: 'none', lg: 'contents' }}>
                  <StickyNote
                    key="sticky-episode"
                    as={motion.div}
                    variants={stageMotion}
                    w={{ base: '320px', '2xl': '420px' }}
                    pos="absolute"
                    inset="20px auto auto 0"
                    initial="hiddenSupport"
                    animate="visible"
                    exit="hiddenSupport"
                    dis
                    sx={{
                      display: { base: 'none', lg: 'block' },
                      '.chakra-heading': {
                        fontSize: { base: '24px', '2xl': '32px' }
                      },
                      '.chakra-text': {
                        fontSize: { base: '16px', '2xl': '24px' }
                      },
                      '.playalong-title': {
                        fontWeight: 800,
                        fontSize: { base: '18px', '2xl': '24px' },
                        textTransform: 'uppercase',
                        mb: '15px',
                        span: {
                          fontWeight: 400,
                          textTransform: 'none'
                        }
                      }
                    }}
                  >
                    <Heading
                      as="div"
                      textAlign="center"
                      lineHeight="0.9"
                      className="playalong-title"
                    >
                      Playalong <span>Presents:</span>
                    </Heading>
                    <Heading
                      as="div"
                      textAlign="center"
                      lineHeight="0.9"
                      sx={{
                        textWrap: 'balance'
                      }}
                    >
                      {episode.title}
                    </Heading>
                    <Flex gap="10px" justifyContent="center">
                      <Img
                        src={episode.series.creator?.channelIcon}
                        w="50vw"
                        h="50vw"
                        maxW="50px"
                        maxH="50px"
                        borderRadius="50px"
                        boxShadow={'0px 0px 3px 2px yellow.400'}
                        alt={episode.series.creator?.name}
                        mb="10px"
                      />
                      <Text
                        as="a"
                        textAlign="left"
                        href={episode.series.creator.qrCode}
                        display="inline-block"
                        mb="0"
                        fontSize="0.8"
                        lineHeight="1"
                        fontWeight={400}
                        target="_blank"
                        sx={{
                          textWrap: 'balance'
                        }}
                      >
                        <strong>{episode.series.name}</strong>
                        <Box whiteSpace="nowrap">
                          by {episode.series.creator.name}
                          <Box
                            as={ExternalLink}
                            verticalAlign="text-top"
                            h="1em"
                            pt="3px"
                            ml="0.25em"
                            mt="1px"
                            display="inline"
                          />
                        </Box>
                      </Text>
                    </Flex>
                    <Box
                      w="100%"
                      backgroundColor="gray.700"
                      h="1px"
                      my="20px"
                    />
                    <Text fontWeight={900} textAlign="center" mb="0" mt="0px">
                      Support The Creators
                    </Text>
                    <Text textAlign="center" fontSize="20px" mt="0">
                      {episode.series.creator.qrCode}
                    </Text>
                    <Flex justifyContent="center" mb="20px">
                      <QRCode
                        fgColor="#2d3748"
                        bgColor="transparent"
                        size={300}
                        value={episode.series.creator.qrCode}
                      />
                    </Flex>
                    <Box
                      w="100%"
                      backgroundColor="gray.700"
                      h="1px"
                      my="20px"
                    />
                    <Text textAlign="center" w="100%">
                      <Button
                        variant="outlineNegative"
                        onClick={backToMenuButton}
                      >
                        Back to Episodes List
                      </Button>
                    </Text>
                  </StickyNote>
                </Box>
              )}
            {showResults && (
              <Box display={{ base: 'none', lg: 'contents' }}>
                <StickyNote
                  key="sticky-results"
                  as={motion.div}
                  variants={stageMotion}
                  w={{ base: '320px', '2xl': '420px' }}
                  pos="absolute"
                  inset="50% auto auto 0"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  <Box h="100%">
                    <Heading textAlign="center">Top Players This Round</Heading>
                    <Grid
                      w="100%"
                      templateColumns="1fr 1fr"
                      sx={{
                        borderRadius: '10px',
                        overflow: 'hidden',
                        '& .th': {
                          bg: 'gray.700',
                          color: 'white',
                          textAlign: 'center',
                          pl: '10px'
                        },
                        '& .tr': {
                          pl: '10px',
                          '&.score': {
                            textAlign: 'center'
                          }
                        },
                        '& .row-even': {
                          bg: 'rgba(0,0,0,0.05)'
                        },
                        '& .row-odd': {
                          bg: 'rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <>
                        <GridItem className="th">Player</GridItem>
                        <GridItem className="th">Score</GridItem>
                      </>
                      {showResults &&
                        currentResults !== '-1' &&
                        Object.entries(previousResults?.result as Result)
                          .sort(([, a], [, b]) => b.score - a.score)
                          .map(([username, player], index) => {
                            return (
                              <React.Fragment key={`player-${username}`}>
                                <GridItem
                                  className={`tr row-${username} row-${
                                    (index + 1) % 2 === 0 ? 'even' : 'odd'
                                  } name`}
                                  textAlign="left"
                                >
                                  {username}
                                </GridItem>
                                <GridItem
                                  className={`tr row-${username} row-${
                                    (index + 1) % 2 === 0 ? 'even' : 'odd'
                                  } score`}
                                >
                                  {player.score}
                                </GridItem>
                              </React.Fragment>
                            )
                          })}
                    </Grid>
                  </Box>
                </StickyNote>
              </Box>
            )}
          </AnimatePresence>
        </Box>

        <ErrorModal
          isOpen={errorMessage !== null}
          onClose={() => {
            setErrorMessage(null)
          }}
        >
          {errorMessage}
        </ErrorModal>
        {/* <ScreenSizePlayalongErrorModal currentEpisode={episode} /> */}
        <Box display={{ base: 'none !important', lg: 'contents' }}>
          <RoomCode
            roomCode={roomCode}
            hide={[GameStates.ATTRACT, GameStates.END].includes(gameState)}
          />
        </Box>
        <SideNav />
      </Box>
    </>
  )
}

const StickyNote = ({ children, sx, ...rest }: any): ReactElement => {
  return (
    <Flex
      sx={{
        bg: 'yellow.300',
        boxShadow: 'sm',
        height: 'auto',
        aspectRatio: '1/1',
        padding: '20px',
        borderRadius: 0,
        flexDirection: 'column',
        ...sx
      }}
      {...rest}
    >
      {children}
    </Flex>
  )
}

const styles = {
  container: {
    opacity: {
      base: 1,
      lg: 1
    },
    backgroundColor: '#181425',
    pos: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%'
  },
  attract: {
    opacity: {
      base: 1,
      lg: 1
    },
    backgroundColor: '#181425',
    width: '100%',
    minHeight: '100dvh',
    '.chakra-heading': {
      color: 'yellow.400'
    },
    '.chakra-text': {
      color: 'yellow.400'
    }
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  csv: {
    position: 'fixed',
    bottom: '0.25em',
    left: '0.25em',
    fontSize: '25px',
    zIndex: 1000
  }
}

const OptionList = ({
  options,
  highlighted,
  chatVote = false,
  showNumbers = false
}: {
  options: string[]
  highlighted?: number
  optionPercentage?: number[]
  chatVote: boolean
  showNumbers?: boolean
}): ReactElement => {
  return (
    <Box mb="0">
      <OrderedList
        listStyleType="none"
        listStylePosition={'inside'}
        fontSize={{ base: '16px', '2xl': '24px' }}
        fontWeight={600}
        marginInlineStart={0}
      >
        {options.map((choice, index) => {
          return (
            <ListItem
              key={`choice-${index}`}
              border="2px solid"
              borderColor="gray.700"
              bg="gray.700"
              opacity={
                highlighted === index || highlighted === undefined ? 1 : 0.5
              }
              color="yellow.300"
              borderRadius="30px"
              p="10px 20px"
            >
              <Flex justifyContent="space-between">
                {showNumbers ? (
                  <span>
                    {index + 1}. &nbsp;
                    {choice}
                  </span>
                ) : (
                  <span>
                    {numericToAlpha(index).toUpperCase()}. &nbsp;
                    {choice}
                  </span>
                )}
                <span
                  style={{
                    whiteSpace: 'nowrap'
                  }}
                >
                  {highlighted === undefined && (
                    <>
                      {chatVote ? (
                        <>
                          !guess
                          {numericToAlpha(index).toUpperCase()}
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </span>
              </Flex>
            </ListItem>
          )
        })}
      </OrderedList>
    </Box>
  )
}

export { Page }

const CircleButton = memo(
  ({
    icon,
    label,
    hoverWidth,
    showHover = false,
    ...rest
  }: CircleButtonProps) => (
    <Button
      display="inline-block"
      variant="circle"
      aria-label={label}
      aspectRatio={1}
      minWidth="2.22em"
      textAlign="left"
      width={{
        base: hoverWidth,
        lg: showHover ? hoverWidth : '2.22em'
      }}
      transition="all 0.5s"
      _hover={{
        width: hoverWidth,
        span: {
          opacity: 1
        }
      }}
      overflow="hidden"
      height="2.22em"
      p="0.33em"
      borderRadius="1.666em"
      colorScheme="yellow"
      color="gray.700"
      fontSize={{
        base: '14px',
        md: '18px',
        lg: '36px'
      }}
      sx={{
        svg: {
          display: 'inline-block',
          width: '1.5em',
          height: 'auto',
          mt: '0px',
          ml: '0px',
          verticalAlign: 'middle'
        }
      }}
      {...rest}
    >
      <Box as={icon} />
      <Box
        as="span"
        verticalAlign="middle"
        opacity={{ base: 1, lg: showHover ? 1 : 0 }}
        className="text"
        color="gray.700"
        ml="0.62em"
      >
        {label}
      </Box>
    </Button>
  )
)

CircleButton.displayName = 'CircleButton'

//
//
//
//
//
//
//
//
//
//
//
//
//
//
